import React, { useEffect, useState } from "react";
import { Building, Edit, AlertCircle } from "lucide-react";
import { useSelector } from "react-redux";
import Card from "../../../../components/ui/card";
import { companySizes, getLabelById, industries, legalStatusOptions, RoleEnum } from "../../../shared";
import Button from "../../../../components/ui/Button";
import { useTranslation } from "react-i18next";

const CompanyClientComponent = ({
    isEditing,
    startEditing,
    handleSaveChanges,
    handleCancelEdit,
    handleCompanyFieldChange,
    editedProfile,
    profileData,
    validationErrors,
    showEditIcon,
    setEditingCompany,
    isProfessional,
    isCurrentUser = true,
    canEdit = true,
    shouldMaskSensitiveInfo = false,
}: any) => {
    const { t } = useTranslation();
    const { user } = useSelector((state: any) => state.auth);
    const idType = editedProfile?.company?.idType || (() => {
        // Derive default idType based on existing values
        // If VAT is validated, auto-select vatNumberVies
        if (editedProfile?.company?.isVatValid && editedProfile?.company?.vatNumber) {
            return "vatNumberVies";
        }
        if (editedProfile?.company?.vatNumber) return "vatNumber";
        return "vatNumber";
    })();

    // Auto-update idType to vatNumberVies when isVatValid becomes true
    useEffect(() => {
        if (editedProfile?.company?.isVatValid && editedProfile?.company?.vatNumber && idType !== "vatNumberVies") {
            handleCompanyFieldChange("idType", "vatNumberVies");
        }
    }, [editedProfile?.company?.isVatValid, editedProfile?.company?.vatNumber]);

    const idTypeToFieldMap: Record<string, { label: string; placeholder: string; field: string }> = {
        taxId: {
            label: t("companyComponent.taxId"),
            placeholder: t("companyComponent.taxIdPlaceholder"),
            field: "vatNumber",
        },
        registrationNumber: {
            label: t("companyComponent.businessNumber"),
            placeholder: t("companyComponent.businessNumberPlaceholder"),
            field: "vatNumber",
        },
        vatNumber: {
            label: t("companyComponent.vatNumberEU"),
            placeholder: t("companyComponent.vatNumberPlaceholder"),
            field: "vatNumber",
        },
        vatNumberVies: {
            label: t("companyComponent.vatNumberVIES"),
            placeholder: t("companyComponent.vatNumberVIESPlaceholder"),
            field: "vatNumber",
        },
    };

    const idConfig = idTypeToFieldMap[idType];

    const handleIdTypeChange = (newType: string) => {
        handleCompanyFieldChange("idType", newType);
    };

    const handleIdValueChange = (value: string) => {
        handleCompanyFieldChange(idConfig.field, value.toUpperCase());
    };

    const isRole4 =
        Number(user?.role?.id) === RoleEnum.professional ||
        Number(user?.role?.id) === RoleEnum.internationl_professional;
    return (
        <Card color={undefined} className="p-4 w-full">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold mb-4">{t("companyComponent.title")}</h2>
                    <p className="text-sm text-gray-500 mb-4">{t("companyComponent.registerAsBusiness")}</p>
                </div>
                {showEditIcon && !isEditing && isCurrentUser && canEdit && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                            if (typeof startEditing === "function") {
                                startEditing();
                            } else if (typeof setEditingCompany === "function") {
                                setEditingCompany();
                            }
                        }}
                        className="text-blue-600 hover:text-blue-700">
                        <Edit className="h-4 w-4" />
                    </Button>
                )}
            </div>
            {isEditing ? (
                <div className="space-y-4">
                    <p>{t("companyComponent.legalRepresentative")}</p>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("companyComponent.selectIdType")}</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="radio"
                                    name="company-id-type"
                                    checked={idType === "taxId"}
                                    onChange={() => handleIdTypeChange("taxId")}
                                />
                                <span>{t("companyComponent.taxId")}</span>
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="radio"
                                    name="company-id-type"
                                    checked={idType === "registrationNumber"}
                                    onChange={() => handleIdTypeChange("registrationNumber")}
                                />
                                <span>{t("companyComponent.businessNumber")}</span>
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="radio"
                                    name="company-id-type"
                                    checked={idType === "vatNumber"}
                                    onChange={() => handleIdTypeChange("vatNumber")}
                                />
                                <span>{t("companyComponent.vatNumberEU")}</span>
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="radio"
                                    name="company-id-type"
                                    checked={idType === "vatNumberVies"}
                                    onChange={() => handleIdTypeChange("vatNumberVies")}
                                />
                                <span>{t("companyComponent.vatNumberVIES")}</span>
                            </label>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{idConfig.label}</label>
                        <input
                            type="text"
                            value={editedProfile?.company?.[idConfig.field] || ""}
                            onChange={e => handleIdValueChange(e.target.value)}
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                            placeholder={idConfig.placeholder}
                        />
                        {validationErrors[idConfig.field] && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors[idConfig.field]}</p>
                        )}
                        {idType.startsWith("vatNumberVies") && (
                            editedProfile.company?.isVatValid ? (
                                <div className="flex items-center text-green-600 text-sm mt-1">
                                    <AlertCircle className="w-4 h-4 mr-1" />
                                    {t("companyComponent.vatNumberVerified")}
                                </div>
                            ) : (
                                <div className="flex items-center text-red-600 text-sm mt-1">
                                    <AlertCircle className="w-4 h-4 mr-1" />
                                    {t("companyComponent.vatNumberNotVerified")}
                                </div>
                            )
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("companyComponent.legalStatus")}</label>
                        <select
                            value={editedProfile?.company?.legalStatus || ""}
                            onChange={e => handleCompanyFieldChange("legalStatus", e.target.value)}
                            className="w-full p-2 border rounded-lg">
                            <option value="">{t("companyComponent.selectLegalStatus")}</option>
                            {legalStatusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        {validationErrors.legalStatus && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.legalStatus}</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("companyComponent.companyName")}</label>
                        <input
                            type="text"
                            value={editedProfile?.company?.name}
                            onChange={e => handleCompanyFieldChange("name", e.target.value)}
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        {validationErrors.name && <p className="text-red-500 text-xs mt-1">{validationErrors.name}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("companyComponent.industry")}</label>
                        <select
                            value={editedProfile?.company?.industry}
                            onChange={e => handleCompanyFieldChange("industry", e.target.value)}
                            className="w-full p-2 border rounded-lg">
                            <option value="">{t("companyComponent.selectIndustry")}</option>
                            {industries.map(industry => (
                                <option key={industry.value} value={industry.value}>
                                    {industry.label}
                                </option>
                            ))}
                        </select>
                        {validationErrors.industry && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.industry}</p>
                        )}
                        {/* <input
                                                                   type="text"
                                                                   value={editedProfile.company.industry}
                                                                   onChange={(e) => handleCompanyFieldChange("industry", e.target.value)}
                                                                   className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                                               /> */}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("companyComponent.size")}</label>
                        <select
                            value={editedProfile?.company?.size}
                            onChange={e => handleCompanyFieldChange("size", e.target.value)}
                            className="w-full p-2 border rounded-lg">
                            <option value="">{t("companyComponent.selectSize")}</option>
                            {companySizes.map(size => (
                                <option key={size.value} value={size.value}>
                                    {size.label}
                                </option>
                            ))}
                        </select>
                        {validationErrors.companySize && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.companySize}</p>
                        )}
                        {/* <input
                            type="text"
                            value={editedProfile.company.size}
                            onChange={e => handleCompanyFieldChange("size", e.target.value)}
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        /> */}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("companyComponent.founded")}</label>
                        <input
                            type="text"
                            value={editedProfile?.company?.founded}
                            onChange={e => handleCompanyFieldChange("founded", e.target.value)}
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        {validationErrors.founded && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.founded}</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("companyComponent.website")}</label>
                        <input
                            type="url"
                            value={editedProfile?.company?.website}
                            onChange={e => handleCompanyFieldChange("website", e.target.value)}
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        {validationErrors.website && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.website}</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("companyComponent.description")}</label>
                        <textarea
                            value={editedProfile?.company?.description}
                            onChange={e => handleCompanyFieldChange("description", e.target.value)}
                            className="w-full p-2 border rounded-lg min-h-[100px] focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        {validationErrors.description && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.description}</p>
                        )}
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={handleCancelEdit} className="text-sm">
                            {t("companyComponent.cancel")}
                        </Button>
                        <Button onClick={handleSaveChanges} className="text-sm">
                            {t("companyComponent.save")}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-2 text-sm">
                    <h3 className="font-medium text-gray-900">{profileData.personal.company.name}</h3>
                    <p className="text-gray-600">{profileData.personal.company.description}</p>
                    <div className="flex justify-between">
                        <span className="text-gray-500">{t("companyComponent.id")}:</span>
                        <span className="font-medium">
                            {(() => {
                                const displayLabel = (() => {
                                    if (profileData.personal.company.vatNumber) return t("companyComponent.vatNumber");
                                    return t("companyComponent.id");
                                })();
                                const displayValue = profileData.personal.company.taxId || profileData.personal.company.registrationNumber || profileData.personal.company.vatNumber || t("companyComponent.notProvided");
                                const maskedValue = shouldMaskSensitiveInfo && displayValue !== t("companyComponent.notProvided") ? "***" : displayValue;
                                return `${displayLabel}: ${maskedValue}`;
                            })()}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-500">{t("companyComponent.legalStatusLabel")}</span>
                        <span className="font-medium">
                            {profileData.personal.company.legalStatus ?
                                profileData.personal.company.legalStatus.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) :
                                t("companyComponent.notProvided")
                            }
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-500">{t("companyComponent.industryLabel")}</span>
                        <span className="font-medium">
                            {" "}
                            <span className="font-medium">
                                {" "}
                                {getLabelById(industries, profileData.personal.company.industry)}
                            </span>
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-500">{t("companyComponent.sizeLabel")}</span>
                        <span className="font-medium">
                            {" "}
                            {getLabelById(companySizes, profileData.personal.company.size)}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-500">{t("companyComponent.foundedLabel")}</span>
                        <span className="font-medium">{profileData.personal.company.founded}</span>
                    </div>
                </div>
            )}
        </Card>
    );
};
export default CompanyClientComponent;
